import path from 'path';
import { FrontmatterMetadata } from '../types.js';
import { ResolvedConfig } from '../utils/config.js';
import { IntentService } from './intent-service.js';

export interface ParsedPathname {
  type: string;
  id: string;
  extension?: string;
  active?: boolean;
  state?: string;
}

export interface FileLocation {
  dirname: string;
  basename: string;
}

/**
 * Service for path manipulation and location determination.
 * Does NOT handle any filesystem operations.
 */
export class PathnameService {
  constructor(
    private config: ResolvedConfig,
    private intentService: IntentService
  ) {}

  // Public getter for config
  getConfig(): ResolvedConfig {
    return this.config;
  }

  // Core Path Operations
  deriveFileLocation(metadata: FrontmatterMetadata): FileLocation {
    const interpretation = this.intentService.interpretIntent({
      pathname: '',  // Empty pathname since we're just interpreting metadata
      metadata
    });

    // Always return relative paths by stripping any leading slashes
    const dirname = interpretation.targetLocation.dirname.replace(/^\/+/, '');
    const basename = interpretation.targetLocation.basename;

    return {
      dirname,
      basename
    };
  }

  joinPath(...parts: string[]): string {
    // Ensure all parts are relative paths by removing any leading slashes
    const relativeParts = parts.map(part => part.replace(/^\/+/, ''));
    return path.join(...relativeParts);
  }

  resolvePath(pathname: string, from?: string): string {
    // Always work with relative paths
    const relativePath = pathname.replace(/^\/+/, '');
    const relativeFrom = from?.replace(/^\/+/, '') || '';
    
    // If from is provided, resolve relative to it, otherwise just normalize the path
    return from ? path.join(relativeFrom, relativePath) : relativePath;
  }

  toCanonicalPath(pathname: string): string {
    // Ensure we're working with a relative path
    const relativePath = pathname.replace(/^\/+/, '');
    return path.normalize(relativePath);
  }

  // Directory Structure
  getActivityDirectories(): string[] {
    const dirs = [
      this.config.inbox_dir || 'inbox',
      this.config.issues_dir || 'issues'
    ];

    // Add active state directories
    for (const state of this.config.active_states) {
      if (state !== 'inbox') {
        dirs.push(path.join(this.config.issues_dir || 'issues', `_${state}`));
      }
    }

    // Add type directories only for types that were explicitly configured
    // Note: We use config.types_list directly since it's no longer modified by IntentService
    for (const type of this.config.types_list) {
      dirs.push(path.join(this.config.issues_dir || 'issues', type));
    }

    return dirs;
  }

  getStateFullPath(state: string): string {
    if (state === 'inbox') {
      return this.config.inbox_dir || 'inbox';
    }
    return path.join(this.config.issues_dir || 'issues', `_${state}`);
  }

  getTypeDirs(): string[] {
    return this.config.types_list.map(type => 
      path.join(this.config.issues_dir || 'issues', type)
    );
  }

  // Path Parsing
  parsePathname(pathname: string): ParsedPathname | null {
    const parts = pathname.split('/');
    const dirname = parts.slice(0, -1).join('/');
    const basename = parts[parts.length - 1];

    // Check if in active state directory
    const isInActiveStateDir = parts.some(part => part.startsWith('_'));
    const activeState = isInActiveStateDir ? parts.find(part => part.startsWith('_'))?.substring(1) : undefined;

    // Match type and id from basename
    const match = basename.match(/^(?:([a-zA-Z0-9]+)-)?([a-zA-Z0-9]+)-([a-zA-Z0-9]+)\..*$/i);
    if (!match) {
      // Special case for filenames without extension
      if (basename.match(/^[a-zA-Z0-9]+$/i)) {
        return {
          id: basename,
          type: '',
          active: this.isInboxDirectory(dirname)
        };
      }
      return null;
    }

    const prefix = match[1];
    const type = match[2];
    const id = match[3];
    const extension = path.extname(basename);

    // If in active state directory, use that state
    if (activeState) {
      return {
        type,
        id,
        extension,
        active: true,
        state: activeState
      };
    }

    // If prefix is an inactive reason, use that
    if (prefix && this.config.inactive_reasons.includes(prefix)) {
      return {
        type,
        id,
        extension,
        active: false,
        state: prefix
      };
    }

    // If in inbox directory, mark as active
    if (this.isInboxDirectory(dirname)) {
      return {
        type,
        id,
        extension,
        active: true,
        state: 'inbox'
      };
    }

    // Default case - in type directory means inactive
    return {
      type,
      id,
      extension,
      active: false
    };
  }

  parseBasename(basename: string): ParsedPathname | null {
    // Match state/reason, type and id from basename
    const match = basename.match(/^([a-zA-Z0-9]+)-([a-zA-Z0-9]+)-([a-zA-Z0-9]+)\..*$/i);
    if (match) {
      const prefix = match[1];
      const type = match[2];
      const id = match[3];

      // If prefix is a known inactive reason, treat it as such
      if (this.config.inactive_reasons.includes(prefix)) {
        return {
          state: prefix,
          type,
          id,
          active: false
        };
      }

      // If prefix is a known active state, treat it as such
      if (this.config.active_states.includes(prefix)) {
        return {
          state: prefix,
          type,
          id,
          active: true
        };
      }

      // Otherwise treat first part as type
      return {
        type: prefix,
        id: type
      };
    }

    // Match type and id without state/reason
    const simpleMatch = basename.match(/^([a-zA-Z0-9]+)-([a-zA-Z0-9]+)\..*$/i);
    if (simpleMatch) {
      return {
        type: simpleMatch[1],
        id: simpleMatch[2]
      };
    }

    // Special case for filenames without extension
    if (basename.match(/^[a-zA-Z0-9]+$/i)) {
      return {
        id: basename,
        type: ''
      };
    }

    return null;
  }

  // Path Generation
  getNewPath(pathname: string, metadata: FrontmatterMetadata): string {
    const location = this.deriveFileLocation(metadata);
    return this.joinPath(location.dirname, location.basename);
  }

  // Directory Type Checks
  isInboxDirectory(dirname: string): boolean {
    return dirname.toLowerCase() === (this.config.inbox_dir || 'inbox').toLowerCase();
  }

  isTypeDirectory(dirname: string): boolean {
    return this.config.types_list.some(type => type.toLowerCase() === dirname.toLowerCase());
  }

  // Path Matching
  matchesId(filename: string, id: string): boolean {
    const parsed = this.parsePathname(filename);
    return parsed ? parsed.id.toLowerCase() === id.toLowerCase() : false;
  }

  // Search Path Generation
  getPossiblePathsForId(id: string): string[] {
    const paths: string[] = [
      this.config.inbox_dir || 'inbox',
      this.config.issues_dir || 'issues',
      ...this.config.active_states
        .filter(state => state !== 'inbox')
        .map(state => path.join(this.config.issues_dir || 'issues', `_${state}`))
    ];

    // Add type directories only for types that were explicitly configured
    // Note: We use config.types_list directly since it's no longer modified by IntentService
    for (const type of this.config.types_list) {
      paths.push(path.join(this.config.issues_dir || 'issues', type));
    }

    return paths;
  }

  getPossiblePathsForType(type: string): string[] {
    return [
      this.config.inbox_dir || 'inbox',
      ...this.config.active_states
        .filter(state => state !== 'inbox')
        .map(state => path.join(this.config.issues_dir || 'issues', `_${state}`)),
      path.join(this.config.issues_dir || 'issues', type)
    ];
  }

  getPossiblePathsForState(state: string): string[] {
    if (state === 'inbox') {
      return [this.config.inbox_dir || 'inbox'];
    }

    // For active states, look in _state directory
    if (this.config.active_states.includes(state)) {
      return [path.join(this.config.issues_dir || 'issues', `_${state}`)];
    }

    // For inactive states (reasons), look in each type directory
    if (this.config.inactive_reasons.includes(state)) {
      return this.config.types_list.map(type => 
        path.join(this.config.issues_dir || 'issues', type)
      );
    }

    // For unknown states, look everywhere
    return this.getActivityDirectories();
  }

  /**
   * Get the canonical path where a NEW file with this ID should be located.
   * This returns the default location for new files (inbox).
   * 
   * To find an existing file by ID, use IssueService.findFileById() instead,
   * as that method handles all the necessary validation and error checking.
   * 
   * @param id The ID to get the path for
   * @returns The canonical path where a new file with this ID should be created
   */
  getPathForId(id: string): string {
    // By default, new IDs go to inbox
    return path.join(this.config.inbox_dir || 'inbox', `${id}.md`);
  }
}